import { useCallback, useRef } from 'react';
import { styled } from 'styled-components';

import { Button } from '#/components/Button/Button';
import { UnstyledNativeLink } from '#/components/Link/UnstyledNativeLink';
import { BodySmall } from '#/components/Text/BodySmall';
import { H2 } from '#/components/Text/H2';
import { SmallTile } from '#/components/Tile/SmallTile';
import { useElementReachedEffect } from '#/hooks/viewport/useElementReachedEffect';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18nLocale } from '#/i18n/types/I18nLocale';
import { salariesPrefix } from '#/modules/salary/routing/salary.paths';
import { colors, cssBreakpoints, fontWeights } from '#/theme/theme';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { trackEventOnce } from '#/utils/analytics/track/trackEventOnce';
import { getStaticImageSrc } from '#/utils/getImageSrc';
import { scrollToTop } from '#/utils/scroll';

import { CoursesAd_Box_1, CoursesAd_Box_2 } from './CoursesAd.styled';

const TileLink = styled(UnstyledNativeLink)`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: block;
  color: ${colors.mediumContentGrey} !important;
  text-decoration: none;
`;

const StyledSearchTile = styled(SmallTile)`
  padding-top: 20px;
  padding-bottom: 30px;
  border-bottom: 4px solid ${colors.brandYellow};

  &:hover a {
    text-decoration-line: underline;
    text-decoration-color: ${colors.hoverBlue};
  }

  @media all and (max-width: ${cssBreakpoints.mdDown}) {
    padding-top: 24px;
    padding-bottom: 24px;
  }
`;

export function CoursesAd() {
  const { i18n, locale } = useI18n();
  const elementRef = useRef<HTMLDivElement>(null);

  useElementReachedEffect({
    ref: elementRef,
    threshold: 0.5,
    onReach: useCallback(() => {
      trackEventOnce('Displayed: IBM Ad', {
        context: 'Interstitial',
      });
    }, []),
  });

  const link =
    locale === I18nLocale.ES
      ? 'https://www.idealist.org/es/ibm-skillsbuild?utm_source=ad&utm_medium=banner&utm_campaign=es_users_es_ibm_opps_interstitial_20260723_ad'
      : 'https://sb-auth.skillsbuild.org/signup?ngo-id=0484&utm_campaign=en_users_ibmskillsbuild_interstitialad';

  return (
    <StyledSearchTile type="button" withHoverEffect qaId="ibm-courses-ad">
      <TileLink
        title={i18n('Sign Up for Free With Idealist')}
        href={link}
        onClick={() => {
          trackClicked('IBM Ad', { context: 'Interstitial' });
        }}
      />
      <CoursesAd_Box_2 ref={elementRef}>
        <BodySmall style={{ color: colors.elementGrey }}>
          {i18n('In collaboration with IBM SkillsBuild')}
        </BodySmall>

        <H2
          style={{
            marginBottom: '4px',
            marginTop: '9px',
            lineHeight: '1.375',
            fontWeight: fontWeights.semiBold,
          }}
        >
          {i18n('Access 1,000 Free Online Courses ✅')}
        </H2>

        <BodySmall>
          {i18n(
            'Gain skills in data analysis, project management, artificial intelligence, and more.',
          )}
        </BodySmall>

        <CoursesAd_Box_1>
          <Button
            type="link"
            variant="tertiary"
            icon={{ name: 'arrow-right', position: 'right' }}
            noPadding
            qaId="ibm-courses-signup-button"
            href={salariesPrefix.fullPath}
            onClick={() => {
              scrollToTop();
              trackClicked('IBM Ad CTA Button', {
                context: 'Interstitial',
              });
            }}
          >
            {i18n('Sign Up for Free With Idealist')}
          </Button>
        </CoursesAd_Box_1>
      </CoursesAd_Box_2>

      <img
        src={getStaticImageSrc('yellow-chart.svg')}
        alt=""
        style={{
          objectFit: 'contain',
          objectPosition: 'right',
          marginRight: '24px',
          width: '100%',
          height: '90px',
          position: 'absolute',
          bottom: '-3px',
          right: 0,
          pointerEvents: 'none',
        }}
      />
    </StyledSearchTile>
  );
}
